/**
 * Checklist Hooks (#45)
 *
 * Provides hooks for checklist template management and
 * booking checklist operations with TanStack Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getI18n } from 'react-i18next';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { queryKeys } from './queryKeys';

// ============================================================================
// TYPES
// ============================================================================

export type ChecklistItemType = 'checkbox' | 'text' | 'photo' | 'numeric';
export type ChecklistStatus = 'pending' | 'in_progress' | 'completed';

export interface ChecklistTemplateItemConfig {
  unit?: string;
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface PhotoConfig {
  allowPhoto?: boolean;
  requirePhoto?: boolean;
  maxPhotos?: number;
}

export interface ChecklistTemplateItem {
  id: string;
  label: string;
  type: ChecklistItemType;
  required: boolean;
  sortOrder: number;
  config?: ChecklistTemplateItemConfig;
  photoConfig?: PhotoConfig;
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  items: ChecklistTemplateItem[];
  serviceIds: string[] | null;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookingChecklistEntry {
  itemId: string;
  checked: boolean;
  value?: string | number;
  notes?: string;
  photoIds?: string[];
  completedAt?: string;
  completedBy?: string;
}

export interface ChecklistPhoto {
  id: string;
  checklistId: string;
  itemId: string;
  fileKey: string;
  fileUrl: string;
  thumbnailUrl: string | null;
  mimeType: string | null;
  fileSizeBytes: number | null;
  caption: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

export interface BookingChecklist {
  id: string;
  bookingId: string;
  templateId: string | null;
  name: string;
  status: ChecklistStatus;
  items: ChecklistTemplateItem[];
  completedItems: BookingChecklistEntry[];
  totalItems: number;
  completedCount: number;
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
  photos?: ChecklistPhoto[];
}

export interface ServiceBookData {
  serviceBookChecked: boolean;
  serviceBookCheckedAt: string | null;
  serviceBookFilled: boolean;
  serviceBookFilledAt: string | null;
  serviceBookFilledBy?: string | null;
  serviceBookType: string | null;
  serviceBookOverrideReason?: string | null;
  serviceBookOverrideAt?: string | null;
  serviceBookOverrideBy?: string | null;
  finalInspectionCompleted: boolean;
  finalInspectionCompletedAt: string | null;
  finalInspectionCompletedBy: string | null;
}

// ============================================================================
// TEMPLATE HOOKS
// ============================================================================

interface TemplateFilters {
  [key: string]: unknown;
  includeInactive?: boolean;
}

export function useChecklistTemplates(filters?: TemplateFilters) {
  return useQuery({
    queryKey: queryKeys.checklistTemplates.list(filters ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.includeInactive) params.set('includeInactive', 'true');
      const qs = params.toString();
      const result = await api.get<{ data: ChecklistTemplate[] }>(
        `/checklist-templates${qs ? `?${qs}` : ''}`,
      );
      return result.data;
    },
  });
}

export function useChecklistTemplate(id: string | null) {
  return useQuery({
    queryKey: queryKeys.checklistTemplates.detail(id ?? ''),
    queryFn: () => api.get<ChecklistTemplate>(`/checklist-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<ChecklistTemplate, 'id' | 'isActive' | 'createdBy' | 'createdAt' | 'updatedAt'>) =>
      api.post<ChecklistTemplate>('/checklist-templates', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.checklistTemplates.all });
    },
  });
}

export function useUpdateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<ChecklistTemplate>) =>
      api.patch<ChecklistTemplate>(`/checklist-templates/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.checklistTemplates.all });
    },
  });
}

export function useDeleteChecklistTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/checklist-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.checklistTemplates.all });
    },
  });
}

export function useVehicleMakesModels() {
  return useQuery({
    queryKey: ['vehicleMakesModels'],
    queryFn: async () => {
      const result = await api.get<{ makes: string[]; models: string[] }>('/vehicles/makes-models');
      return result;
    },
  });
}

// ============================================================================
// BOOKING CHECKLIST HOOKS
// ============================================================================

export function useBookingChecklists(bookingId: string | null) {
  return useQuery({
    queryKey: queryKeys.checklists.byBooking(bookingId ?? ''),
    queryFn: async () => {
      const result = await api.get<{ data: BookingChecklist[] }>(
        `/checklists/by-booking/${bookingId}`,
      );
      return result.data;
    },
    enabled: !!bookingId,
  });
}

export function useBookingChecklist(id: string | null) {
  return useQuery({
    queryKey: queryKeys.checklists.detail(id ?? ''),
    queryFn: () => api.get<BookingChecklist>(`/checklists/${id}`),
    enabled: !!id,
  });
}

export function useCreateBookingChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { bookingId: string; templateId?: string; name?: string }) =>
      api.post<BookingChecklist>('/checklists', data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.byBooking(variables.bookingId),
      });
    },
  });
}

export function useAutoCreateChecklists() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) =>
      api.post<{ data: BookingChecklist[]; count: number }>(`/checklists/auto/${bookingId}`),
    onSuccess: (_data, bookingId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.byBooking(bookingId),
      });
    },
  });
}

export interface UpdateChecklistItemVariables {
  checklistId: string;
  itemId: string;
  checked: boolean;
  value?: string | number;
  notes?: string;
}

/**
 * Pure helper that mirrors the staff-api `/checklists/:id/items` mutation:
 * upserts a single entry into `completedItems` and recomputes the derived
 * `completedCount` / `status` fields without mutating the source checklist.
 *
 * Keeping this deterministic means rapid successive row updates applied on top
 * of each other never corrupt `completedItems` locally.
 */
export function applyChecklistItemUpdate(
  checklist: BookingChecklist,
  entry: Omit<UpdateChecklistItemVariables, 'checklistId'>,
): BookingChecklist {
  const existingEntries = checklist.completedItems ?? [];
  const index = existingEntries.findIndex((e) => e.itemId === entry.itemId);

  const previous = index >= 0 ? existingEntries[index] : undefined;
  const updatedEntry: BookingChecklistEntry = {
    ...previous,
    itemId: entry.itemId,
    checked: entry.checked,
  };
  if (entry.value !== undefined) updatedEntry.value = entry.value;
  if (entry.notes !== undefined) updatedEntry.notes = entry.notes;
  if (entry.checked) {
    updatedEntry.completedAt = updatedEntry.completedAt ?? new Date().toISOString();
  } else {
    delete updatedEntry.completedAt;
    delete updatedEntry.completedBy;
  }

  const nextEntries =
    index >= 0
      ? existingEntries.map((e, i) => (i === index ? updatedEntry : e))
      : [...existingEntries, updatedEntry];

  const completedCount = nextEntries.filter((e) => e.checked).length;
  const newStatus: ChecklistStatus = completedCount === 0 ? 'pending' : 'in_progress';

  return {
    ...checklist,
    completedItems: nextEntries,
    completedCount,
    // Preserve a finished checklist's terminal status, mirroring the backend.
    status: checklist.status === 'completed' ? 'completed' : newStatus,
  };
}

interface UpdateChecklistItemContext {
  previousDetail?: BookingChecklist;
  previousList?: BookingChecklist[];
  bookingId?: string;
}

type QueryClientType = ReturnType<typeof useQueryClient>;

/**
 * Builds the TanStack mutation options for an optimistic checklist item update.
 * Extracted from the hook so the optimistic / rollback / invalidation behavior
 * can be unit tested without a React renderer.
 */
export function buildUpdateChecklistItemMutation(
  queryClient: QueryClientType,
  notify: { error: (title: string, message?: string) => void },
) {
  return {
    mutationFn: ({ checklistId, ...entry }: UpdateChecklistItemVariables) =>
      api.patch<BookingChecklist>(`/checklists/${checklistId}/items`, entry),
    onMutate: async ({ checklistId, ...entry }: UpdateChecklistItemVariables) => {
      const detailKey = queryKeys.checklists.detail(checklistId);

      // Cancel in-flight detail fetches so they don't overwrite our optimistic value.
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousDetail = queryClient.getQueryData<BookingChecklist>(detailKey);
      let bookingId = previousDetail?.bookingId;

      // Optimistically update the checklist detail cache.
      if (previousDetail) {
        queryClient.setQueryData<BookingChecklist>(
          detailKey,
          applyChecklistItemUpdate(previousDetail, entry),
        );
      }

      // Mechanic task cards often have only the by-booking list cached (no
      // detail query). Find that list by checklist id + item id so a tick
      // still patches it (#2936).
      if (!bookingId) {
        await queryClient.cancelQueries({
          queryKey: [...queryKeys.checklists.all, 'booking'],
        });
        const listQueries = queryClient.getQueriesData<BookingChecklist[]>({
          queryKey: [...queryKeys.checklists.all, 'booking'],
        });
        for (const [, list] of listQueries) {
          if (!Array.isArray(list)) continue;
          const found = list.find(
            (c) =>
              c.id === checklistId &&
              c.items.some((item) => item.id === entry.itemId),
          );
          if (found) {
            bookingId = found.bookingId;
            break;
          }
        }
      }

      // Optimistically update the booking checklist list cache (if loaded).
      let previousList: BookingChecklist[] | undefined;
      if (bookingId) {
        const listKey = queryKeys.checklists.byBooking(bookingId);
        await queryClient.cancelQueries({ queryKey: listKey });
        previousList = queryClient.getQueryData<BookingChecklist[]>(listKey);
        if (previousList) {
          queryClient.setQueryData<BookingChecklist[]>(
            listKey,
            previousList.map((c) =>
              c.id === checklistId ? applyChecklistItemUpdate(c, entry) : c,
            ),
          );
        }
      }

      const context: UpdateChecklistItemContext = { previousDetail, previousList, bookingId };
      return context;
    },
    onError: (
      _error: Error,
      variables: UpdateChecklistItemVariables,
      context: UpdateChecklistItemContext | undefined,
    ) => {
      // Roll back both caches to their pre-mutation snapshots.
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.checklists.detail(variables.checklistId),
          context.previousDetail,
        );
      }
      if (context?.previousList && context.bookingId) {
        queryClient.setQueryData(
          queryKeys.checklists.byBooking(context.bookingId),
          context.previousList,
        );
      }
      // No useTranslation in a hook, so the message comes from the shared
      // instance. `getI18n()` rather than importing '@/lib/i18n': that module
      // runs the whole i18next bootstrap on import, which breaks any test
      // that mocks react-i18next (#2716).
      //
      // Optional-chained because this runs inside a rollback: if i18n is not
      // initialised, the caches must still be restored rather than the whole
      // handler throwing on the way to showing a message.
      const key = 'checklistItemUpdateFailed';
      notify.error(getI18n()?.t(key, { ns: 'checklists' }) ?? key);
    },
    onSuccess: (data: BookingChecklist) => {
      // Sync caches with the authoritative server response immediately so other
      // open views relying on these caches see the canonical value.
      queryClient.setQueryData(queryKeys.checklists.detail(data.id), data);
    },
    onSettled: (
      data: BookingChecklist | undefined,
      _error: Error | null,
      variables: UpdateChecklistItemVariables,
      context: UpdateChecklistItemContext | undefined,
    ) => {
      const checklistId = data?.id ?? variables.checklistId;
      const bookingId = data?.bookingId ?? context?.bookingId;
      // Revalidate from the server so any other open work order/checklist view
      // refreshes through the normal invalidation path.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.detail(checklistId),
      });
      if (bookingId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.checklists.byBooking(bookingId),
        });
      }
    },
  };
}

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation<
    BookingChecklist,
    Error,
    UpdateChecklistItemVariables,
    UpdateChecklistItemContext
  >(buildUpdateChecklistItemMutation(queryClient, notify));
}

export function useCompleteChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (checklistId: string) =>
      api.post<BookingChecklist>(`/checklists/${checklistId}/complete`),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.detail(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.byBooking(data.bookingId),
      });
    },
  });
}

export function useDeleteBookingChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; bookingId: string }) =>
      api.delete(`/checklists/${id}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.byBooking(variables.bookingId),
      });
    },
  });
}

// ============================================================================
// PHOTO HOOKS
// ============================================================================

export interface ChecklistPhoto {
  id: string;
  checklistId: string;
  itemId: string;
  fileKey: string;
  fileUrl: string;
  thumbnailUrl: string | null;
  mimeType: string | null;
  fileSizeBytes: number | null;
  caption: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

export function useChecklistPhotoUploadUrl() {
  return useMutation({
    mutationFn: ({
      checklistId,
      itemId,
      mimeType,
      fileExtension,
    }: {
      checklistId: string;
      itemId: string;
      mimeType: string;
      fileExtension?: string;
    }) =>
      api.post<{ uploadUrl: string; fileKey: string; fileUrl: string }>(
        `/checklists/${checklistId}/photos/upload-url`,
        { itemId, mimeType, fileExtension }
      ),
  });
}

export function useRegisterChecklistPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      ...data
    }: {
      checklistId: string;
      itemId: string;
      fileKey: string;
      fileUrl: string;
      mimeType?: string;
      fileSizeBytes?: number;
      caption?: string;
    }) => api.post<ChecklistPhoto>(`/checklists/${checklistId}/photos`, data),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.detail(data.checklistId),
      });
    },
  });
}

export function useDeleteChecklistPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      photoId,
    }: {
      checklistId: string;
      photoId: string;
      bookingId?: string;
    }) => api.delete(`/checklists/${checklistId}/photos/${photoId}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checklists.detail(variables.checklistId),
      });
      if (variables.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.checklists.byBooking(variables.bookingId),
        });
      }
    },
  });
}

// ============================================================================
// SERVICE BOOK HOOK
// ============================================================================

export function useUpdateServiceBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      ...data
    }: {
      workOrderId: string;
      serviceBookChecked?: boolean;
      serviceBookFilled?: boolean;
      serviceBookType?: 'digital' | 'paper' | 'both' | null;
      finalInspectionCompleted?: boolean;
      overrideReason?: string;
    }) => api.patch<ServiceBookData>(`/work-orders/${workOrderId}/service-book`, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
    },
  });
}

// ── REPORT ──────────────────────────────────────────────────────────────

interface ChecklistReportResult {
  fileUrl: string;
  fileKey: string;
  downloadUrl: string;
}

export function useGenerateChecklistReport() {
  return useMutation({
    mutationFn: ({ checklistId, language = 'fi' }: { checklistId: string; language?: 'fi' | 'en' }) =>
      api.post<ChecklistReportResult>(`/checklists/${checklistId}/report`, { language }),
  });
}
